import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { isAxiosError } from 'axios';
import { getHash } from '@kizenapps/engine/util';
import type {
  CompleteSetupLevel,
  HostCompleteSetupFn,
  SetupAssistantConfig,
  UnknownJSON,
} from '@kizenapps/engine';
import PluginService from 'services/PluginService';
import TeamMemberService from 'services/TeamMemberService';
import { MarketplacePlugin } from 'pages/Marketplace/types';
import { BusinessPluginApp } from './types';

export interface ActiveSetupSurface {
  pluginApiName: string;
  level: CompleteSetupLevel;
  onComplete?: () => void;
}

type RegisteredSetupSurface = ActiveSetupSurface & {
  sequence: number;
};

type ActiveSetupSurfaceRegistry = Map<string, Set<RegisteredSetupSurface>>;

const ActiveSetupSurfaceContext =
  createContext<ActiveSetupSurfaceRegistry | null>(null);

export const ActiveSetupSurfaceProvider = ActiveSetupSurfaceContext.Provider;

const registryKey = (pluginApiName: string, level: CompleteSetupLevel) =>
  `${pluginApiName}:${level}`;

let registrationSequence = 0;

const isCompleteSetupLevel = (value: unknown): value is CompleteSetupLevel =>
  value === 'business' || value === 'user';

const getLiveSurfaces = (
  registry: ActiveSetupSurfaceRegistry,
  pluginApiName: string,
  level: CompleteSetupLevel
): RegisteredSetupSurface[] => [
  ...(registry.get(registryKey(pluginApiName, level)) ?? []),
];

const resolveLevel = (
  registry: ActiveSetupSurfaceRegistry,
  pluginApiName: string,
  requestedLevel: unknown
): CompleteSetupLevel => {
  const liveSurfaces = [
    ...getLiveSurfaces(registry, pluginApiName, 'business'),
    ...getLiveSurfaces(registry, pluginApiName, 'user'),
  ];

  if (liveSurfaces.length) {
    return liveSurfaces.reduce((latest, surface) =>
      surface.sequence > latest.sequence ? surface : latest
    ).level;
  }

  if (isCompleteSetupLevel(requestedLevel)) {
    return requestedLevel;
  }

  return 'business';
};

type EmployeePluginConfig = {
  config?: Record<string, unknown> & {
    user_config?: Record<string, unknown>;
  };
};

const buildStoredConfig = (
  existingConfig: Record<string, unknown> | undefined,
  payload: UnknownJSON,
  assistantConfig: SetupAssistantConfig | undefined
): Record<string, unknown> => {
  const stored: Record<string, unknown> = {
    ...existingConfig,
    __kizen_clean_config: payload,
  };

  if (assistantConfig) {
    stored.__kizen_setup_assistant_hash = getHash(
      JSON.stringify(assistantConfig)
    );
  }

  return stored;
};

const persistBusinessSetup = async (
  pluginApp: MarketplacePlugin,
  payload: UnknownJSON,
  businessPluginApps: BusinessPluginApp[]
) => {
  const pluginApiName = pluginApp.api_name;

  const businessPluginApp = businessPluginApps.find(
    (app) => app.plugin_app.api_name === pluginApiName
  );

  if (!businessPluginApp) {
    throw new Error(
      `Unable to load the stored configuration for plugin app "${pluginApiName}"`
    );
  }

  await PluginService.updateBusinessPluginApp(pluginApiName, {
    config: buildStoredConfig(
      businessPluginApp.config,
      payload,
      pluginApp.base_config?.setup_assistant
    ),
  });
};

const getEmployeePluginConfig = async (
  pluginId: string
): Promise<EmployeePluginConfig> => {
  try {
    return await TeamMemberService.getPluginConfig(pluginId);
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return {};
    }
    throw error;
  }
};

const persistUserSetup = async (
  pluginApp: MarketplacePlugin,
  payload: UnknownJSON
) => {
  const existing = await getEmployeePluginConfig(pluginApp.id);

  await TeamMemberService.updatePluginConfig(pluginApp.id, {
    ...existing,
    config: {
      ...existing.config,
      user_config: buildStoredConfig(
        existing.config?.user_config,
        payload,
        pluginApp.base_config?.user_setup_assistant
      ),
    },
  });
};

export const useRegisterActiveSetupSurface = (
  surface: ActiveSetupSurface | null
) => {
  const registry = useContext(ActiveSetupSurfaceContext);
  const pluginApiName = surface?.pluginApiName;
  const level = surface?.level;
  const onComplete = surface?.onComplete;

  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    if (!registry || !pluginApiName || !level) {
      return;
    }

    const entry: RegisteredSetupSurface = {
      pluginApiName,
      level,
      onComplete: () => onCompleteRef.current?.(),
      sequence: ++registrationSequence,
    };

    const key = registryKey(pluginApiName, level);
    const surfaces = registry.get(key) ?? new Set<RegisteredSetupSurface>();
    surfaces.add(entry);
    registry.set(key, surfaces);

    return () => {
      const current = registry.get(key);

      if (!current) {
        return;
      }

      current.delete(entry);

      if (!current.size) {
        registry.delete(key);
      }
    };
  }, [registry, pluginApiName, level]);
};

export const useCompleteSetup = (
  refreshPluginArtifacts: (
    pluginApiName?: string,
    options?: { pluginAppId?: string }
  ) => Promise<void>
) => {
  const registryRef = useRef<ActiveSetupSurfaceRegistry>(new Map());

  const refreshPluginArtifactsRef = useRef(refreshPluginArtifacts);

  useEffect(() => {
    refreshPluginArtifactsRef.current = refreshPluginArtifacts;
  }, [refreshPluginArtifacts]);

  const onCompleteSetup = useCallback<HostCompleteSetupFn>(
    async (pluginApiName, payload, options) => {
      const level = resolveLevel(
        registryRef.current,
        pluginApiName,
        options?.level
      );

      const [pluginApp, businessPluginApps]: [
        MarketplacePlugin,
        BusinessPluginApp[] | undefined,
      ] = await Promise.all([
        PluginService.getPluginApp(pluginApiName),
        level === 'business'
          ? PluginService.getBusinessPluginApps()
          : undefined,
      ]);

      if (level === 'user') {
        await persistUserSetup(pluginApp, payload);
      } else {
        await persistBusinessSetup(
          pluginApp,
          payload,
          businessPluginApps ?? []
        );
      }

      await refreshPluginArtifactsRef.current(pluginApiName, {
        pluginAppId: pluginApp.id,
      });

      getLiveSurfaces(registryRef.current, pluginApiName, level).forEach(
        (surface) => surface.onComplete?.()
      );
    },
    []
  );

  return { onCompleteSetup, activeSetupSurfaces: registryRef.current };
};
